import { db } from '@/lib/firebase';
import { collection, doc, getDoc, getDocs, setDoc, query, where, writeBatch, DocumentReference } from 'firebase/firestore';

export interface FrameworkVersion {
    clauses: Array<string | { __ref: string | DocumentReference }>;
    controls: Array<string | { __ref: string | DocumentReference }>;
    tasks: Array<string | { __ref: string | DocumentReference }>;
    version: string;
    designators?: string[];
    rootKey?: string;
    components?: Array<{ __ref: string | DocumentReference }>;
}

export interface Framework {
    aliases: string[];
    displayName: string;
    framework: {
        name: string;
    };
    patterns: string[];
    slug: string;
    version: FrameworkVersion;
}

/**
 * Gets a framework by its ID from the modules collection
 */
export async function getFramework(frameworkId: string): Promise<Framework | null> {
    const docRef = doc(db, 'modules', frameworkId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return { id: docSnap.id, ...docSnap.data() } as Framework;
}

/**
 * Copies framework controls to a customer's SOA
 * For ISM and EssentialEight, controls are stored in ismsSoa/ism/{CONTROLID}
 * For other frameworks, controls are stored directly in ismsSoa/controls/{CONTROLID}
 */
export async function copyControlsToCustomerSOA(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string }
): Promise<void> {
    try {
        console.log(`Copying controls to customer SOA for framework ${frameworkId}`);
        const framework = await getFramework(frameworkId);
        if (!framework) {
            throw new Error(`Framework ${frameworkId} not found`);
        }

        const batch = writeBatch(db);
        const soaPath = `customers/${customerId}/product/policyPackages/ISO27001keyDocuments/ismsSoa`;

        // Check if this is ISM or EssentialEight
        const isSpecialFramework = ['ISM', 'EssentialEight'].includes(frameworkId);

        console.log(`Framework Data: ${JSON.stringify(framework)}`);
        // Check if we have a root key
        const rootKey = framework.version.rootKey || null;
        // if (!rootKey) {
        //     throw new Error(`Framework ${frameworkId} does not have a root key`);
        // }
        // Process each control reference
        for (const controlRef of framework.version.controls) {
            let refPath: string | undefined;

            if (typeof controlRef === 'string') {
                // Direct string path
                refPath = controlRef;
            } else if (controlRef && typeof controlRef === 'object') {
                // Handle { __ref: string | DocumentReference }
                if ('__ref' in controlRef) {
                    const ref = controlRef.__ref;
                    if (typeof ref === 'string') {
                        refPath = ref;
                    } else if (ref?.path) {
                        refPath = ref.path;
                    }
                } else if (controlRef.path) {
                    // Direct DocumentReference
                    refPath = controlRef.path;
                }
            }

            if (!refPath) {
                console.error('Invalid control reference format:', controlRef);
                continue;
            }

            // Get the control document
            const controlDoc = await getDoc(doc(db, refPath));
            if (!controlDoc.exists()) {
                console.error(`Control ${refPath} not found`);
                continue;
            };

            // Create the control in the customer's SOA
            const controlData = rootKey ? controlDoc.data()[rootKey] : controlDoc.data();
            console.log('Framework Data 2: ', controlData);
            // const controlId = refPath.split('/').pop();

            // Determine the target path based on framework
            for (const [key, value] of Object.entries(controlData)) {
                // console.log(key, value);
                if (matchParams) {
                    for (const [paramKey, paramValue] of Object.entries(matchParams)) {
                        if (value[paramKey] !== paramValue) {
                            console.log('Skipping control', key, value, paramKey, paramValue);
                            continue;
                        }
                    }
                }

                if (isSpecialFramework) {
                    const targetPath = `${soaPath}/ism/${key}`;

                    const customerControlRef = doc(db, targetPath);

                    batch.set(customerControlRef, {
                        ...value,
                        sourceFramework: frameworkId,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    }, { merge: true });
                } else {
                    const targetPath = `${soaPath}`;
                    const customerControlRef = doc(db, targetPath);

                    console.log('Customer Control Ref: ', customerControlRef);

                    let dataToSave = {};

                    dataToSave = {
                        [value.controlID || value.controlId || key]: {
                            ...value,
                            sourceFramework: frameworkId,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                        }
                    };

                    console.log('Adding Control: ', dataToSave);

                    batch.set(customerControlRef, dataToSave, { merge: true })
                }
            }
        }

        await batch.commit().then(() => {
            console.log('Controls copied successfully');
        }).catch((error) => {
            console.error('Error copying controls to customer SOA:', error);
            throw error;
        });
    } catch (error) {
        console.error('Error copying controls to customer SOA:', error);
        throw error;
    }
}

/**
 * Copies framework tasks to a customer's assurance tasks
 * @param matchParams - Simple key-value matching for filtering tasks
 * @param arrayInclusionFilter - Filter for checking if a value exists in an array field
 *   Example: { path: 'checkDetail.e8Details.e8LevelCoverage', value: '2' }
 */
export async function copyTasksToCustomerAssurance(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string },
    arrayInclusionFilter?: { path: string; value: any } | string
): Promise<void> {
    // Parse arrayInclusionFilter if it's a string (from UI)
    let parsedFilter: { path: string; value: any } | undefined;
    
    console.log('Raw arrayInclusionFilter:', arrayInclusionFilter, 'Type:', typeof arrayInclusionFilter);
    
    if (typeof arrayInclusionFilter === 'string') {
        try {
            parsedFilter = JSON.parse(arrayInclusionFilter);
            console.log('Parsed filter:', parsedFilter);
        } catch (e) {
            console.error('Failed to parse arrayInclusionFilter:', e);
            throw new Error('arrayInclusionFilter must be a valid JSON object or an object with path and value properties');
        }
    } else {
        parsedFilter = arrayInclusionFilter;
    }

    // Validate arrayInclusionFilter if provided
    if (parsedFilter) {
        console.log('Validating parsedFilter:', parsedFilter, 'path:', parsedFilter.path);
        if (!parsedFilter.path || typeof parsedFilter.path !== 'string') {
            throw new Error(`arrayInclusionFilter.path must be a non-empty string. Got: ${JSON.stringify(parsedFilter)}`);
        }
    }

    const framework = await getFramework(frameworkId);
    if (!framework) {
        throw new Error(`Framework ${frameworkId} not found`);
    }

    const batch = writeBatch(db);
    const assurancePath = `customers/${customerId}/compliance/assurance`;
    const now = new Date().toISOString();

    // Process each task reference
    for (const taskRef of framework.version.tasks) {
        // Handle multiple task reference formats
        let refPath: string | undefined;

        if (typeof taskRef === 'string') {
            // Direct string path
            refPath = taskRef;
        } else if (typeof taskRef === 'object' && taskRef !== null) {
            if ('path' in taskRef && typeof taskRef.path === 'string') {
                // This is a DocumentReference object
                refPath = taskRef.path;
            } else if ('__ref' in taskRef) {
                // Handle { __ref: string | DocumentReference } format
                const ref = taskRef.__ref;
                if (typeof ref === 'string') {
                    refPath = ref;
                } else if (ref && typeof ref === 'object' && 'path' in ref) {
                    refPath = ref.path;
                }
            }
        }

        if (!refPath || typeof refPath !== 'string') {
            console.error('Invalid task reference format:', taskRef);
            continue;
        }

        // Get the task template
        const taskDoc = await getDoc(doc(db, refPath));
        if (!taskDoc.exists()) {
            console.warn(`Task document not found at path: ${refPath}`);
            continue;
        }

        // Create the task in the customer's assurance collection
        const taskData = taskDoc.data();
        console.log('Task Data: ', taskData);
        // const taskId = refPath.split('/').pop();
        const customerTaskRef = doc(db, assurancePath);

        for (const [key, value] of Object.entries(taskData)) {
            console.log(key, value);
            
            // Apply simple key-value matching filter
            if (matchParams) {
                let matchFailed = false;
                for (const [paramKey, paramValue] of Object.entries(matchParams)) {
                    if (value[paramKey] !== paramValue) {
                        matchFailed = true;
                        break;
                    }
                }
                if (matchFailed) continue;
            }
            
            // Apply array inclusion filter (e.g., check if "2" is in e8LevelCoverage array)
            if (parsedFilter && parsedFilter.path) {
                const pathParts = parsedFilter.path.split('.');
                let current = value;
                
                // Navigate through the nested path
                for (const part of pathParts) {
                    if (current && typeof current === 'object' && part in current) {
                        current = current[part];
                    } else {
                        current = undefined;
                        break;
                    }
                }
                
                // Check if the value exists in the array
                if (!Array.isArray(current) || !current.includes(parsedFilter.value)) {
                    console.log(`Task ${key} filtered out: ${parsedFilter.value} not in ${parsedFilter.path}`);
                    continue;
                }
            }
            
            // DED-1511: Ensure tasks are active (enabled) by default when copied
            const taskValue = { ...value };
            if (taskValue.checkDetail && typeof taskValue.checkDetail === 'object') {
                taskValue.checkDetail = {
                    ...taskValue.checkDetail,
                    active: 'true', // Ensure task is enabled by default
                };
            }
            
            // one of these are probably redundant right

            // DED-1640: Ensure all copied tasks are active by default
            batch.set(customerTaskRef, {
                [key]: {
                    ...taskValue,
                    sourceFramework: frameworkId,
                    checkDetail: {
                        ...value.checkDetail,
                        active: 'Yes' // Ensure all copied tasks are active by default
                    }
                },
            }, { merge: true });
        }
    }

    await batch.commit();
}

/**
 * Copies all framework components (controls and tasks) to a customer's collections
 */
export async function copyFrameworkToCustomer(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string }
): Promise<{ controlsCount: number; tasksCount: number }> {
    await copyControlsToCustomerSOA(customerId, frameworkId, matchParams);
    await copyTasksToCustomerAssurance(customerId, frameworkId, matchParams);

    // Count the number of controls and tasks copied
    const framework = await getFramework(frameworkId);
    const controlsCount = framework?.version.controls.length || 0;
    const tasksCount = framework?.version.tasks.length || 0;

    return { controlsCount, tasksCount };
}

/**
 * Helper to calculate due date based on current date and days to add
 */
function calculateDueDate(now: string, daysToAdd?: number): string {
    if (!daysToAdd) return '';

    const date = new Date(now);
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString();
}

/**
 * Finds a framework by its name or alias
 */
export async function findFrameworkByName(name: string): Promise<Framework | null> {
    // First try exact match on ID
    const framework = await getFramework(name);
    if (framework) return framework;

    // Then search by name or aliases
    const frameworksRef = collection(db, 'modules');
    const q = query(
        frameworksRef,
        where('aliases', 'array-contains', name)
    );

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() } as Framework;
    }

    return null;
}

export async function listFrameworks(): Promise<Framework[]> {
    const frameworksRef = collection(db, 'modules');
    const q = query(frameworksRef);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    })) as Framework[];
}